import { lazy, type ComponentType } from 'react';

/**
 * lazy() wrapper that survives a deploy happening between page load and
 * navigation. Every deploy replaces every JS chunk's filename (content
 * hash) — if a visitor's already-loaded app shell tries to dynamically
 * import() a chunk from a PREVIOUS deploy, that file is gone from the CDN
 * (404), the import() promise rejects, and React routes that straight to
 * the nearest ErrorBoundary ("Coś poszło nie tak") with no way to recover
 * short of a manual reload. A single automatic reload fixes it: it fetches
 * the CURRENT index.html, which references the CURRENT chunk hashes.
 * Guarded with sessionStorage so a genuinely broken chunk (not just a stale
 * one) gets exactly one retry, never an infinite reload loop; the guard
 * clears itself on the next successful chunk load, so a later, unrelated
 * failure later in the session still gets its own fresh retry.
 */
const CHUNK_RELOAD_KEY = 'bk:chunk-reload-attempted';

export function lazyWithReload<T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>,
) {
  return lazy(() =>
    factory()
      .then(mod => {
        try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ }
        return mod;
      })
      .catch(err => {
        let alreadyTried = false;
        try { alreadyTried = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1'; } catch { /* ignore */ }
        if (!alreadyTried) {
          try { sessionStorage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { /* ignore */ }
          window.location.reload();
          // Reload is navigating away — never resolve, nothing left to render.
          return new Promise<T>(() => {});
        }
        throw err;
      }),
  );
}
